/**
 * Registers a service worker whose scope does not contain the page, and
 * waits until it can serve requests.
 *
 * `navigator.serviceWorker.ready` must not be used for this: it resolves
 * only for a registration whose scope covers the current page. The worker
 * of the registration itself tells when it is activated (workers here call
 * skipWaiting and clients.claim, so activation follows installation).
 */
export async function registerScopedServiceWorker(scriptUrl, scope) {
    if (!('serviceWorker' in navigator)) {
        throw new Error('service workers are not supported');
    }
    const registration = await navigator.serviceWorker.register(scriptUrl, {
        scope,
        updateViaCache: 'none',
    });
    const worker = registration.active ?? registration.waiting ?? registration.installing;
    if (worker && worker.state !== 'activated') {
        await new Promise((resolve, reject) => {
            const onStateChange = () => {
                if (worker.state === 'activated') {
                    worker.removeEventListener('statechange', onStateChange);
                    resolve();
                }
                else if (worker.state === 'redundant') {
                    worker.removeEventListener('statechange', onStateChange);
                    // a newer worker took over while this one installed
                    if (registration.active) {
                        resolve();
                    }
                    else {
                        reject(new Error('service worker became redundant'));
                    }
                }
            };
            worker.addEventListener('statechange', onStateChange);
            onStateChange();
        });
    }
    return registration;
}
/**
 * The registration whose scope is exactly `scope`, if the browser still
 * has it. A browser may remove a registration on its own: Safari's
 * tracking prevention does, for a site the user has not interacted with.
 */
export async function getScopedServiceWorkerRegistration(scope) {
    if (!('serviceWorker' in navigator)) {
        return undefined;
    }
    const url = new URL(scope, window.location.href).href;
    try {
        // the lookup is by longest prefix: take only the scope itself
        const registration = await navigator.serviceWorker.getRegistration(url);
        return registration?.scope === url ? registration : undefined;
    }
    catch {
        return undefined;
    }
}
